// Shared helper to turn an unknown error (typically axios) into a
// human-readable string for inline form errors / toasts.
//
// Backed by the centralised `describeBackendReason` mapper in
// `@leyrochain/shared`. The backend returns `reason` codes like
// `buyer_kyc_not_found`; the mapper translates them to user-facing
// galician text. Raw codes should never reach the UI.

import axios from 'axios';
import { describeBackendReason } from '@leyrochain/shared';

interface BackendError {
  reason?: string;
  error?: string;
  message?: string;
}

export function describeAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as BackendError | undefined;
    // 1. Try the centralised mapper first — covers known reason codes
    //    (`buyer_kyc_not_found`, `stripe_connect_required`, etc.).
    if (data?.reason) {
      return describeBackendReason(data.reason, fallback);
    }
    // 2. Fall back to error → message → axios message → fallback. These
    //    branches only trigger when the backend didn't send `reason` —
    //    e.g. low-level network errors or non-JSON 5xx responses.
    return data?.error ?? data?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
